"use client";

/**
 * Step 1 — "Bienvenue". Purely presentational (no form, no store writes;
 * the wizard logic in layout.tsx keeps canContinue=true for index===1).
 *
 * The marketing pitch (why Guydeez / how it works / testimonial) lives on
 * its own page at /becoming-guide — this step is just the wizard welcome:
 * a photo card with the live stats overlay (real countries / cities +
 * the multilingual argument, same cached source as the homepage globe and
 * the /becoming-guide hero) + the welcome text and checklist.
 *
 *  - DESKTOP (lg+): 2-column (welcome left, stats photo right).
 *  - MOBILE (<lg): stacked (stats photo, then welcome + checklist).
 */

import React, { FC, useEffect, useMemo } from "react";
import Image from "next/image";
import guidesPhoto from "@/images/homepage/groupTour.jpg";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { getCountries } from "@/app/lib/reducers/countriesReducer";

const CheckCircle: FC = () => (
  <span
    className="grid place-items-center"
    style={{
      width: 20,
      height: 20,
      borderRadius: "50%",
      background: "rgba(31,122,69,.12)",
      flexShrink: 0,
    }}
    aria-hidden="true"
  >
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--g-green-ada, #1F7A45)"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  </span>
);

const Recruitment1: FC = () => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();

  // Real stats — derived from the cached countries graph, exactly like the
  // homepage globe and the /becoming-guide hero. One fetch per session
  // (guarded by `if (!countries.length)`), so no perf hit on revisit.
  const selectedLanguage =
    useAppSelector((s: any) => s?.language?.selectedLanguage) || "en_US";
  const countries = useAppSelector((s: any) => s?.countries?.countries ?? []);

  useEffect(() => {
    if (!countries?.length) dispatch(getCountries(null, true, selectedLanguage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedLanguage, countries?.length]);

  const counts = useMemo(() => {
    const active = (countries as any[]).filter((c: any) => c.activated);
    return {
      countries: active.length,
      cities: active.reduce((acc: number, c: any) => acc + (c.numCities || 0), 0),
    };
  }, [countries]);

  const fmt = (n: number) =>
    n.toLocaleString(String(selectedLanguage).replace("_", "-"));

  const checklist: string[] = [
    t("recruitment_welcome_check1", {
      defaultValue: "Profil personnel et coordonnées",
    }) as string,
    t("recruitment_welcome_check2", {
      defaultValue: "Votre ville et votre approche",
    }) as string,
    t("recruitment_welcome_check3", {
      defaultValue: "Photo + 5 audios de présentation",
    }) as string,
  ];

  // Countries + cities (real) + the multilingual argument.
  const stats: Array<{ value: string; label: string }> = [
    {
      value: counts.countries > 0 ? fmt(counts.countries) : "—",
      label: t("becoming_guide_stat_countries_l", { defaultValue: "pays" }) as string,
    },
    {
      value: counts.cities > 0 ? fmt(counts.cities) : "—",
      label: t("becoming_guide_stat_cities_l", { defaultValue: "villes" }) as string,
    },
    {
      value: t("becoming_guide_stat_languages_n", { defaultValue: "7" }) as string,
      label: t("becoming_guide_stat_languages_l", { defaultValue: "langues" }) as string,
    },
  ];

  // Reusable bits ----------------------------------------------------------
  const Eyebrow: FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
      style={{
        fontSize: 11,
        color: "var(--g-coral, #C13B5C)",
        fontWeight: 700,
        letterSpacing: ".12em",
        textTransform: "uppercase",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );

  const StatsImageCard: FC<{ minHeight?: number }> = ({ minHeight = 200 }) => (
    <div
      style={{
        position: "relative",
        borderRadius: 16,
        overflow: "hidden",
        minHeight,
        boxShadow: "var(--g-shadow-md, 0 18px 40px rgba(0,0,0,.16))",
      }}
    >
      <Image
        src={guidesPhoto}
        alt=""
        fill
        sizes="(max-width: 1024px) 100vw, 50vw"
        style={{ objectFit: "cover" }}
        aria-hidden
      />
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.72) 100%)",
        }}
      />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "20px 20px", color: "white" }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            opacity: 0.85,
            marginBottom: 10,
          }}
        >
          {t("recruitment_welcome_stats_eyebrow", {
            defaultValue: "Guydeez en un coup d'œil",
          })}
        </div>
        <div className="flex" style={{ gap: 18, flexWrap: "wrap" }}>
          {stats.map((s, i) => (
            <div key={i}>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, opacity: 0.8, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const WelcomeText: FC = () => (
    <div>
      <Eyebrow>{t("recruitment_welcome_eyebrow", { defaultValue: "BIENVENUE" })}</Eyebrow>
      <h2
        style={{
          fontSize: "clamp(24px, 6vw, 30px)",
          color: "var(--g-burgundy)",
          fontWeight: 800,
          lineHeight: 1.12,
          marginBottom: 14,
        }}
      >
        {t("recruitment_welcome_title", { defaultValue: "Devenez guide local Guydeez." })}
      </h2>
      <p style={{ fontSize: 14, color: "var(--g-ink-700, #4a4a4a)", lineHeight: 1.6, marginBottom: 12 }}>
        {t("recruitment_welcome_intro1", {
          defaultValue:
            "Merci pour votre intérêt à devenir guide local avec Guydeez ! Nous aimerions en savoir plus sur vous et votre ville.",
        })}
      </p>
      <p style={{ fontSize: 14, color: "var(--g-ink-500)", lineHeight: 1.6, marginBottom: 18 }}>
        {t("recruitment_welcome_intro2_a", { defaultValue: "Compléter le formulaire prend environ " })}
        <strong style={{ color: "var(--g-burgundy)" }}>
          {t("recruitment_welcome_minutes", { defaultValue: "10 minutes" })}
        </strong>
        {t("recruitment_welcome_intro2_b", { defaultValue: "." })}
      </p>
      <ul className="flex flex-col" style={{ gap: 12, marginBottom: 16, listStyle: "none", padding: 0 }}>
        {checklist.map((item, i) => (
          <li key={i} className="flex items-center" style={{ gap: 12, fontSize: 14, color: "var(--g-ink-900, #1a1a1a)" }}>
            <CheckCircle />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p style={{ fontSize: 12, fontStyle: "italic", color: "var(--g-ink-500)" }}>
        {t("recruitment_welcome_languages_note", {
          defaultValue: "* Formulaire disponible en FR, EN, ES, IT.",
        })}
      </p>
    </div>
  );

  return (
    <>
      {/* ===================== DESKTOP (lg+) ===================== */}
      <div
        className="hidden lg:grid lg:grid-cols-2"
        style={{ gap: 36, alignItems: "center" }}
      >
        <WelcomeText />
        <StatsImageCard minHeight={340} />
      </div>

      {/* ===================== MOBILE (<lg) ===================== */}
      <div className="lg:hidden flex flex-col" style={{ gap: 24 }}>
        <StatsImageCard minHeight={190} />
        <WelcomeText />
      </div>
    </>
  );
};

export default Recruitment1;
